import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  RESPONSE_INIT,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AffaldHubRegion, RecyclingService } from '@services/http/recycling.service';

/**
 * Consolidated affald hub hierarchy (structural SEO audit, Task 3).
 *
 * Renders region → kommune → by with a direct link to each existing leaf page
 * (genbrugsstationer / storskrald / haveaffald). Rendered on /affald/hub, it
 * replaces the four retired index pages as the single crawl entry point: every
 * city leaf is one click from the hub, and station-detail pages are reachable
 * via their genbrugsstationer (city-list) page — so no affald leaf is more than
 * 3 clicks from the hub.
 *
 * The affald universe is Danish-only, so the route segments are hardcoded by
 * design (no locale route helper).
 */
@Component({
  selector: 'app-affald-hub-hierarchy',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (regions.length > 0) {
      <section class="affald-hub">
        <header class="affald-hub__intro">
          <h2>Find affaldsinformation efter område</h2>
          <p>
            Vælg din by for at se genbrugsstationer, storskrald og haveaffald i
            hele Danmark.
          </p>
          <nav class="affald-hub__regionnav" aria-label="Områder">
            @for (region of regions; track region.regionSlug) {
              <a class="affald-hub__regionnav-link" [href]="'#omraade-' + region.regionSlug">
                {{ region.regionName }}
              </a>
            }
          </nav>
        </header>

        @for (region of regions; track region.regionSlug) {
          <div class="affald-hub__region" [id]="'omraade-' + region.regionSlug">
            <h3 class="affald-hub__region-title">{{ region.regionName }}</h3>
            @for (muni of region.municipalities; track muni.municipality) {
              <div class="affald-hub__kommune">
                <h4 class="affald-hub__kommune-title">
                  {{ muni.municipality }} kommune
                  <span class="affald-hub__count">{{ muni.cities.length }} byer</span>
                </h4>
                <ul class="affald-hub__cities">
                  @for (city of muni.cities; track city.citySlug) {
                    <li class="affald-hub__city">
                      <span class="affald-hub__city-name">{{ city.city }}</span>
                      <span class="affald-hub__chips">
                        @if (city.leaves.genbrugsstationer) {
                          <a
                            class="affald-hub__chip"
                            [routerLink]="['/affald', region.regionSlug, city.citySlug, 'genbrugsstationer']"
                            >Genbrugsstationer</a
                          >
                        }
                        @if (city.leaves.storskrald) {
                          <a
                            class="affald-hub__chip"
                            [routerLink]="['/affald', region.regionSlug, city.citySlug, 'storskrald']"
                            >Storskrald</a
                          >
                        }
                        @if (city.leaves.haveaffald) {
                          <a
                            class="affald-hub__chip"
                            [routerLink]="['/affald', 'affald-have', region.regionSlug, city.citySlug, 'haveaffald']"
                            >Haveaffald</a
                          >
                        }
                      </span>
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
        }
      </section>
    }
  `,
  styles: [
    `
      .affald-hub {
        margin: 2.5rem auto 0;
        max-width: 1100px;
        padding: 0 1.5rem 1rem;
        box-sizing: border-box;
      }
      @media (max-width: 600px) {
        .affald-hub {
          padding: 0 1rem 1rem;
        }
      }

      .affald-hub__intro h2 {
        color: var(--grey_6);
        margin-bottom: 0.5rem;
      }
      .affald-hub__intro p {
        color: var(--grey_5);
        margin: 0 0 1.25rem;
        max-width: 48ch;
      }

      /* Region quick-nav */
      .affald-hub__regionnav {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        margin-bottom: 2rem;
      }
      .affald-hub__regionnav-link {
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--blue_5);
        background: var(--grey_2);
        padding: 0.35rem 0.85rem;
        border-radius: 999px;
        text-decoration: none;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .affald-hub__regionnav-link:hover {
        background: var(--blue_2);
        color: #fff;
      }

      /* Region */
      .affald-hub__region {
        scroll-margin-top: 1.5rem;
        margin-bottom: 2.5rem;
      }
      .affald-hub__region-title {
        color: var(--grey_6);
        font-size: 1.5rem;
        padding-bottom: 0.5rem;
        border-bottom: 3px solid var(--green_1);
        margin-bottom: 1.25rem;
      }

      /* Kommune */
      .affald-hub__kommune {
        margin-bottom: 1.5rem;
      }
      .affald-hub__kommune-title {
        display: flex;
        align-items: baseline;
        gap: 0.6rem;
        color: var(--grey_6);
        font-size: 1.05rem;
        margin: 0 0 0.75rem;
      }
      .affald-hub__count {
        font-size: 0.8rem;
        font-weight: 500;
        color: var(--grey_4);
      }

      /* City cards grid */
      .affald-hub__cities {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: 0.75rem;
      }
      .affald-hub__city {
        background: #fff;
        border: 1px solid var(--grey_2);
        border-radius: 12px;
        padding: 0.85rem 1rem;
        transition: box-shadow 0.15s ease, transform 0.15s ease;
      }
      .affald-hub__city:hover {
        box-shadow: 0 4px 14px rgba(13, 41, 61, 0.1);
        transform: translateY(-1px);
      }
      .affald-hub__city-name {
        display: block;
        font-weight: 700;
        color: var(--grey_6);
        margin-bottom: 0.5rem;
      }

      /* Leaf chips */
      .affald-hub__chips {
        display: flex;
        flex-wrap: wrap;
        gap: 0.4rem;
      }
      .affald-hub__chip {
        font-size: 0.78rem;
        font-weight: 600;
        line-height: 1;
        color: var(--blue_5);
        background: rgba(27, 154, 198, 0.12);
        padding: 0.35rem 0.6rem;
        border-radius: 999px;
        text-decoration: none;
        white-space: nowrap;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .affald-hub__chip:hover {
        background: var(--blue_2);
        color: #fff;
      }

      @media (max-width: 600px) {
        .affald-hub__cities {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class AffaldHubHierarchyComponent implements OnInit, OnDestroy {
  regions: AffaldHubRegion[] = [];
  loading = true;

  private readonly recyclingService = inject(RecyclingService);
  private readonly cdr = inject(ChangeDetectorRef);
  // Server-only (null in the browser). Lets us flag an SSR failure so the
  // incomplete, linkless hub is not cached as a healthy 200.
  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.recyclingService
      .getAffaldHub()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (regions) => {
          this.regions = regions ?? [];
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          // On SSR, a failed hierarchy fetch leaves a linkless hub. Flag 503 so
          // the incomplete page isn't cached as healthy (mirrors HubPageComponent).
          if (this.responseInit) {
            this.responseInit.status = 503;
          }
          this.cdr.markForCheck();
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
